import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Calendar as CalIcon, Users, Clock, ChevronLeft, ChevronRight, ChevronDown,
  LogOut, Phone, Mail, MessageSquare, Check, X, Trash2, Loader2, Plus, Send, Bell, BellOff, Pencil,
} from "lucide-react";
import { format, addDays, subDays, parseISO } from "date-fns";
import { it } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mama_admin_token";

const STATUS_LABEL = {
  pending: "In attesa",
  confirmed: "Confermata",
  cancelled: "Annullata",
  completed: "Completata",
};
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-300 line-through",
  completed: "bg-slate-100 text-slate-700 border-slate-300",
};

// ---------- Login ----------
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onLogin(res.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password non corretta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE4] flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-3xl border border-[#1E3A5D]/15 shadow-xl p-8 sm:p-10"
      >
        <img src="/brand/logo-mama.png" alt="Mama" className="h-20 mx-auto mb-6" />
        <h1 className="font-script text-4xl text-[#1E3A5D] text-center leading-none">Pannello prenotazioni</h1>
        <p className="mt-3 text-center text-[#1E3A5D]/60 font-serif-body text-sm">Accesso riservato al ristorante</p>

        <div className="mt-8">
          <Label htmlFor="pw" className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">PASSWORD</Label>
          <Input
            id="pw" type="password" autoFocus
            data-testid="admin-password-input"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-2"
          />
        </div>
        <Button
          type="submit" disabled={loading || !password}
          data-testid="admin-login-btn"
          className="mt-6 w-full font-display text-xs tracking-[0.22em] rounded-full bg-[#B59410] hover:bg-[#8E7308] text-white py-6 font-semibold"
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Entra
        </Button>
      </form>
    </div>
  );
};

// ---------- Reservation card ----------
// Mappa prefissi telefonici → lingua del messaggio di conferma
const _prefixToLang = (phone) => {
  const digits = (phone || "").replace(/[^0-9]/g, "");
  if (!digits) return "it";
  // Ordine: prefissi più lunghi prima
  if (digits.startsWith("351")) return "pt"; // Portogallo
  if (digits.startsWith("55"))  return "pt"; // Brasile
  if (digits.startsWith("43"))  return "de"; // Austria
  if (digits.startsWith("49"))  return "de"; // Germania
  if (digits.startsWith("41"))  return "de"; // Svizzera (DE-CH prevalente per turismo)
  if (digits.startsWith("36"))  return "hu"; // Ungheria
  if (digits.startsWith("39"))  return "it"; // Italia
  if (digits.startsWith("378")) return "it"; // San Marino
  // Prefissi anglofoni comuni per turisti
  if (digits.startsWith("1"))   return "en"; // USA/Canada
  if (digits.startsWith("44"))  return "en"; // UK
  if (digits.startsWith("353")) return "en"; // Irlanda
  if (digits.startsWith("61"))  return "en"; // Australia
  return "en"; // Default per turisti stranieri
};

const _confirmMsg = {
  it: (r, dateFmt, guests) =>
    `Ciao ${r.name}! 👋\n\n` +
    `Ti confermiamo la prenotazione a *mAmA Cucina & Pizza*:\n\n` +
    `📅 ${dateFmt}\n🕐 Ore ${r.time}\n👥 ${guests}` +
    (r.notes ? `\n📝 Note: ${r.notes}` : "") + "\n\n" +
    `📍 Via Udine 81, Lignano Sabbiadoro\n☎️ +39 379 183 0524\n\n` +
    `Ti aspettiamo! 🍕\n\n` +
    `━━━━━━━━━━━━━━\n*Policy prenotazione*\n` +
    `• Tolleriamo un ritardo massimo di *7 minuti*.\n` +
    `• Il tavolo viene assegnato e confermato solo se all'orario stabilito il gruppo è al completo: se manca anche solo una persona la prenotazione decade.\n\n` +
    `_Sono regole necessarie per lavorare con rispetto anche degli altri ospiti che hanno prenotato e per avere il tempo di seguire con attenzione tutti i nostri clienti._`,
  en: (r, dateFmt, guests) =>
    `Hi ${r.name}! 👋\n\n` +
    `Your booking at *mAmA Cucina & Pizza* is confirmed:\n\n` +
    `📅 ${dateFmt}\n🕐 ${r.time}\n👥 ${guests}` +
    (r.notes ? `\n📝 Notes: ${r.notes}` : "") + "\n\n" +
    `📍 Via Udine 81, Lignano Sabbiadoro (Italy)\n☎️ +39 379 183 0524\n\n` +
    `See you soon! 🍕\n\n` +
    `━━━━━━━━━━━━━━\n*Booking policy*\n` +
    `• We allow a maximum delay of *7 minutes*.\n` +
    `• The table is assigned and confirmed only if the full group is present at the reserved time: if even one person is missing, the booking is cancelled.\n\n` +
    `_These rules ensure we can respect the other guests who booked and take proper care of everyone in our restaurant._`,
  de: (r, dateFmt, guests) =>
    `Hallo ${r.name}! 👋\n\n` +
    `Ihre Reservierung im *mAmA Cucina & Pizza* ist bestätigt:\n\n` +
    `📅 ${dateFmt}\n🕐 ${r.time} Uhr\n👥 ${guests}` +
    (r.notes ? `\n📝 Anmerkungen: ${r.notes}` : "") + "\n\n" +
    `📍 Via Udine 81, Lignano Sabbiadoro (Italien)\n☎️ +39 379 183 0524\n\n` +
    `Wir freuen uns auf Sie! 🍕\n\n` +
    `━━━━━━━━━━━━━━\n*Reservierungsrichtlinie*\n` +
    `• Wir tolerieren eine maximale Verspätung von *7 Minuten*.\n` +
    `• Der Tisch wird nur zugewiesen und bestätigt, wenn die vollständige Gruppe zur reservierten Zeit anwesend ist: fehlt auch nur eine Person, verfällt die Reservierung.\n\n` +
    `_Diese Regeln ermöglichen es uns, auch die anderen Gäste zu respektieren und alle unsere Kunden aufmerksam zu betreuen._`,
  pt: (r, dateFmt, guests) =>
    `Olá ${r.name}! 👋\n\n` +
    `A sua reserva no *mAmA Cucina & Pizza* está confirmada:\n\n` +
    `📅 ${dateFmt}\n🕐 ${r.time}\n👥 ${guests}` +
    (r.notes ? `\n📝 Notas: ${r.notes}` : "") + "\n\n" +
    `📍 Via Udine 81, Lignano Sabbiadoro (Itália)\n☎️ +39 379 183 0524\n\n` +
    `Até já! 🍕\n\n` +
    `━━━━━━━━━━━━━━\n*Regras da reserva*\n` +
    `• Toleramos um atraso máximo de *7 minutos*.\n` +
    `• A mesa é atribuída e confirmada apenas se o grupo estiver completo à hora reservada: se faltar mesmo uma só pessoa, a reserva é anulada.\n\n` +
    `_Estas regras permitem-nos respeitar os outros clientes que reservaram e cuidar com atenção de todos os nossos hóspedes._`,
  hu: (r, dateFmt, guests) =>
    `Szia ${r.name}! 👋\n\n` +
    `A foglalásod a *mAmA Cucina & Pizza* étterembe megerősítve:\n\n` +
    `📅 ${dateFmt}\n🕐 ${r.time}\n👥 ${guests}` +
    (r.notes ? `\n📝 Megjegyzés: ${r.notes}` : "") + "\n\n" +
    `📍 Via Udine 81, Lignano Sabbiadoro (Olaszország)\n☎️ +39 379 183 0524\n\n` +
    `Várunk szeretettel! 🍕\n\n` +
    `━━━━━━━━━━━━━━\n*Foglalási szabályok*\n` +
    `• Maximum *7 perc* késést tolerálunk.\n` +
    `• Az asztalt csak akkor foglaljuk le és erősítjük meg, ha a teljes társaság a lefoglalt időpontban jelen van: ha akár egy fő is hiányzik, a foglalás érvényét veszti.\n\n` +
    `_Ezek a szabályok lehetővé teszik számunkra, hogy tiszteletben tartsuk a többi vendégünket és figyelmesen kiszolgáljuk minden ügyfelünket._`,
};

const _localeFor = { it: it, en: undefined, de: undefined, pt: undefined, hu: undefined };
// Note: date-fns locales beyond "it" aren't imported here to keep bundle small;
// English format is the default and is universally readable for numeric dates.

const _buildWaConfirmMessage = (r) => {
  const lang = _prefixToLang(r.phone);
  const locale = _localeFor[lang];
  const dateFmt = (() => {
    try {
      return locale
        ? format(parseISO(r.date), "EEEE d MMMM", { locale })
        : format(parseISO(r.date), "EEEE, MMMM d");
    } catch { return r.date; }
  })();
  const peopleLabels = {
    it: r.people === 1 ? "persona" : "persone",
    en: r.people === 1 ? "person" : "people",
    de: r.people === 1 ? "Person" : "Personen",
    pt: r.people === 1 ? "pessoa" : "pessoas",
    hu: r.people === 1 ? "fő" : "fő",
  };
  const guests = `${r.people} ${peopleLabels[lang]}`;
  const builder = _confirmMsg[lang] || _confirmMsg.it;
  return builder(r, dateFmt, guests);
};

const ReservationCard = ({ r, onUpdate, onDelete, onEdit }) => {
  const color = STATUS_COLOR[r.status] || STATUS_COLOR.pending;
  const waPhone = (r.phone || "").replace(/[^0-9]/g, "");
  const waConfirmUrl = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent(_buildWaConfirmMessage(r))}`
    : null;
  return (
    <div
      data-testid={`res-card-${r.id}`}
      className={`bg-white rounded-xl border p-4 shadow-sm hover:shadow-md transition-shadow ${
        r.status === "cancelled" ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="font-display text-sm text-[#1E3A5D] tracking-[0.1em] font-semibold">
            {r.time} · {r.name}
          </p>
          <p className="mt-0.5 text-xs text-[#1E3A5D]/60 font-serif-body">
            {r.people} {r.people === 1 ? "persona" : "persone"}
            {r.requires_confirmation && (
              <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-[#B59410]/15 text-[#B59410] rounded font-semibold">
                Gruppo 6+
              </span>
            )}
          </p>
        </div>
        <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border font-semibold ${color}`}>
          {STATUS_LABEL[r.status] || r.status}
        </span>
      </div>

      <div className="mt-2 space-y-1 text-xs text-[#1E3A5D]/80 font-serif-body">
        <p className="flex items-center gap-1.5">
          <Phone className="w-3 h-3 text-[#B59410]" />
          <a href={`tel:${r.phone}`} className="hover:text-[#B59410]">{r.phone}</a>
          {r.phone && (
            <a
              href={`https://wa.me/${r.phone.replace(/[^0-9]/g, "")}`}
              target="_blank" rel="noopener noreferrer"
              className="ml-1 text-[#25D366]"
              title="WhatsApp"
            >
              <MessageSquare className="w-3 h-3" />
            </a>
          )}
        </p>
        {r.email && (
          <p className="flex items-center gap-1.5">
            <Mail className="w-3 h-3 text-[#B59410]" />
            <a href={`mailto:${r.email}`} className="hover:text-[#B59410]">{r.email}</a>
          </p>
        )}
        {r.notes && (
          <p className="italic text-[#1E3A5D]/70 pt-1 border-t border-[#1E3A5D]/10 mt-2">
            📝 {r.notes}
          </p>
        )}
      </div>

      {waConfirmUrl && r.status !== "cancelled" && (
        <a
          href={waConfirmUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`btn-wa-confirm-${r.id}`}
          className="mt-3 flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-semibold bg-[#25D366] hover:bg-[#1DA851] text-white rounded transition-colors"
          title={`Apre WhatsApp con testo di conferma in lingua ${_prefixToLang(r.phone).toUpperCase()}`}
        >
          <MessageSquare className="w-3.5 h-3.5" /> Conferma via WhatsApp
          <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-white/25 rounded-full font-bold">
            {_prefixToLang(r.phone).toUpperCase()}
          </span>
        </a>
      )}

      <div className="mt-3 flex gap-1.5 flex-wrap">
        {r.status !== "confirmed" && (
          <button
            onClick={() => onUpdate(r.id, "confirmed")}
            data-testid={`btn-confirm-${r.id}`}
            className="flex-1 min-w-0 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded transition-colors flex items-center justify-center gap-1"
          >
            <Check className="w-3 h-3" /> Conferma
          </button>
        )}
        {r.status !== "completed" && (
          <button
            onClick={() => onUpdate(r.id, "completed")}
            data-testid={`btn-complete-${r.id}`}
            className="flex-1 min-w-0 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-slate-600 hover:bg-slate-700 text-white rounded transition-colors"
          >
            Fatta
          </button>
        )}
        {r.status !== "cancelled" && (
          <button
            onClick={() => onUpdate(r.id, "cancelled")}
            data-testid={`btn-cancel-${r.id}`}
            className="flex-1 min-w-0 px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded transition-colors flex items-center justify-center gap-1"
          >
            <X className="w-3 h-3" /> Annulla
          </button>
        )}
        <button
          onClick={() => onEdit(r)}
          data-testid={`btn-edit-${r.id}`}
          className="px-2 py-1.5 text-[10px] text-[#1E3A5D] hover:bg-[#F5EFE4] rounded transition-colors flex items-center gap-1"
          title="Modifica"
        >
          <Pencil className="w-3 h-3" /> <span className="hidden sm:inline">Modifica</span>
        </button>
        <button
          onClick={() => window.confirm("Eliminare definitivamente?") && onDelete(r.id)}
          data-testid={`btn-delete-${r.id}`}
          className="px-2 py-1.5 text-[10px] text-rose-700 hover:bg-rose-50 rounded transition-colors"
          title="Elimina"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ---------- Telegram notifications card ----------
const TelegramCard = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!status) return null;
  if (!status.bot_configured) return null;

  const register = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/telegram/register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data.registered) {
        toast.success(`Telegram collegato a ${r.data.user || "te"}`);
        await load();
      } else {
        toast.info(r.data.message || "Apri il bot e invia /start, poi riprova");
      }
    } catch {
      toast.error("Errore registrazione");
    } finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/telegram/test`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Messaggio di test inviato su Telegram");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio test");
    } finally { setBusy(false); }
  };

  const unregister = async () => {
    if (!window.confirm("Scollegare le notifiche Telegram?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/telegram/register`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Telegram scollegato");
      await load();
    } catch {
      toast.error("Errore");
    } finally { setBusy(false); }
  };

  const configured = status.chat_id_configured;
  return (
    <section
      data-testid="telegram-card"
      className={`mt-6 rounded-2xl border p-4 sm:p-5 shadow-sm ${
        configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${
            configured ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
          }`}>
            {configured ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="font-script text-xl text-[#1E3A5D] leading-none">
              Notifiche Telegram
            </h3>
            {configured ? (
              <p className="mt-1 text-xs text-emerald-800 font-serif-body">
                Attivo · ricevi un messaggio push ad ogni prenotazione
              </p>
            ) : (
              <div className="mt-1 text-xs text-amber-900 font-serif-body space-y-1">
                <p>Bot pronto ma serve <strong>un ultimo passaggio</strong>:</p>
                <ol className="list-decimal ml-4">
                  <li>Apri Telegram e cerca <strong>@mama_cucina_bot</strong></li>
                  <li>Clicca <strong>Avvia</strong> (o scrivi <code>/start</code>)</li>
                  <li>Torna qui e clicca <strong>Registra</strong> qui accanto</li>
                </ol>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!configured && (
            <Button
              onClick={register}
              disabled={busy}
              data-testid="telegram-register-btn"
              className="rounded-full text-xs font-display tracking-wider bg-amber-600 hover:bg-amber-700 text-white font-semibold"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Registra
            </Button>
          )}
          {configured && (
            <>
              <Button
                onClick={test}
                disabled={busy}
                data-testid="telegram-test-btn"
                variant="outline"
                className="rounded-full text-xs font-display tracking-wider"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Invia test
              </Button>
              <Button
                onClick={unregister}
                disabled={busy}
                data-testid="telegram-unregister-btn"
                variant="outline"
                className="rounded-full text-xs font-display tracking-wider text-red-600 border-red-200 hover:bg-red-50"
              >
                Scollega
              </Button>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ---------- Manual reservation modal ----------
// Generate all 5-min slots to match backend TURNS configuration
const _genSlots = (sh, sm, eh, em) => {
  const out = [];
  let t = sh * 60 + sm;
  const end = eh * 60 + em;
  while (t <= end) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    t += 5;
  }
  return out;
};
const ALL_TIME_SLOTS = [
  ..._genSlots(11, 30, 15, 0),   // Pranzo
  ..._genSlots(18, 30, 19, 25),  // Cena 1
  ..._genSlots(19, 30, 20, 25),  // Cena 2
  ..._genSlots(20, 30, 21, 25),  // Cena 3
  ..._genSlots(21, 30, 22, 30),  // Cena 4
];

const ManualReservationModal = ({ token, defaultDate, editRes, onClose, onCreated }) => {
  const isEdit = Boolean(editRes);
  const [form, setForm] = useState({
    name: editRes?.name || "",
    phone: editRes?.phone || "",
    email: editRes?.email || "",
    date: editRes?.date || defaultDate,
    time: editRes?.time || "20:00",
    people: editRes?.people ?? 2,
    notes: editRes?.notes || "",
    force: false,
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [sugField, setSugField] = useState(null); // 'name' | 'phone'
  const searchTimer = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const searchCustomers = (q, sourceField) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.trim().length < 2 || isEdit) {
      setSuggestions([]); setShowSug(false); return;
    }
    setSugField(sourceField);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/customers/search`, {
          params: { q, limit: 6 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestions(r.data.customers || []);
        setShowSug((r.data.customers || []).length > 0);
      } catch { /* ignore */ }
    }, 220);
  };

  const applyCustomer = (c) => {
    setForm((f) => ({
      ...f,
      name: c.name || f.name,
      phone: c.phone || f.phone,
      email: c.email || f.email,
    }));
    setShowSug(false);
    setSuggestions([]);
    toast.success(`Cliente caricato · ${c.visits}x visite`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Nome e telefono sono obbligatori");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        date: form.date,
        time: form.time,
        people: Number(form.people),
        notes: form.notes.trim(),
      };
      if (isEdit) {
        await axios.put(`${API}/admin/reservations/${editRes.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Prenotazione aggiornata");
      } else {
        await axios.post(`${API}/admin/reservations`, { ...payload, force: form.force }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Prenotazione aggiunta");
      }
      onCreated();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || (isEdit ? "Errore aggiornamento" : "Errore inserimento");
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="manual-res-modal-backdrop"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        data-testid="manual-res-form"
        className="w-full max-w-lg bg-white rounded-2xl border border-[#1E3A5D]/15 shadow-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-script text-3xl text-[#1E3A5D] leading-none">
              {isEdit ? "Modifica prenotazione" : "Nuova prenotazione"}
            </h2>
            <p className="mt-1 text-xs text-[#1E3A5D]/60 font-serif-body">
              {isEdit ? "Aggiorna i dati e salva" : "Inserimento manuale (telefono / walk-in)"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="manual-res-close-btn"
            className="w-8 h-8 rounded-full grid place-items-center hover:bg-[#F5EFE4]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">NOME *</Label>
            <Input
              value={form.name}
              onChange={(e) => { update("name", e.target.value); searchCustomers(e.target.value, "name"); }}
              onFocus={(e) => { if (e.target.value) searchCustomers(e.target.value, "name"); }}
              onBlur={() => setTimeout(() => setShowSug(false), 180)}
              data-testid="manual-res-name"
              className="mt-1.5" required autoComplete="off"
            />
            {showSug && sugField === "name" && suggestions.length > 0 && (
              <div data-testid="cust-suggestions-name" className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-[#1E3A5D]/15 shadow-lg max-h-56 overflow-y-auto">
                {suggestions.map((c, i) => (
                  <button
                    key={c.id || i}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyCustomer(c)}
                    data-testid={`cust-sug-${i}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5EFE4] border-b border-[#1E3A5D]/5 last:border-0"
                  >
                    <p className="text-sm text-[#1E3A5D] font-semibold">{c.name}</p>
                    <p className="text-xs text-[#1E3A5D]/60">{c.phone} · {c.visits}x visite</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="relative">
              <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">TELEFONO *</Label>
              <Input
                value={form.phone}
                onChange={(e) => { update("phone", e.target.value); searchCustomers(e.target.value, "phone"); }}
                onFocus={(e) => { if (e.target.value) searchCustomers(e.target.value, "phone"); }}
                onBlur={() => setTimeout(() => setShowSug(false), 180)}
                data-testid="manual-res-phone"
                placeholder="+39 ..."
                className="mt-1.5" required autoComplete="off"
              />
              {showSug && sugField === "phone" && suggestions.length > 0 && (
                <div data-testid="cust-suggestions-phone" className="absolute z-10 top-full left-0 right-0 mt-1 bg-white rounded-lg border border-[#1E3A5D]/15 shadow-lg max-h-56 overflow-y-auto">
                  {suggestions.map((c, i) => (
                    <button
                      key={c.id || i}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyCustomer(c)}
                      data-testid={`cust-sug-phone-${i}`}
                      className="w-full text-left px-3 py-2 hover:bg-[#F5EFE4] border-b border-[#1E3A5D]/5 last:border-0"
                    >
                      <p className="text-sm text-[#1E3A5D] font-semibold">{c.name}</p>
                      <p className="text-xs text-[#1E3A5D]/60">{c.phone} · {c.visits}x visite</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">EMAIL</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="manual-res-email"
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">DATA *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => update("date", e.target.value)}
                data-testid="manual-res-date"
                className="mt-1.5" required
              />
            </div>
            <div>
              <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">ORA *</Label>
              <select
                value={form.time}
                onChange={(e) => update("time", e.target.value)}
                data-testid="manual-res-time"
                className="mt-1.5 w-full h-9 px-3 rounded-md border border-[#1E3A5D]/20 text-sm bg-white"
                required
              >
                {ALL_TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">PERSONE *</Label>
              <Input
                type="number" min="1" max="100"
                value={form.people}
                onChange={(e) => update("people", e.target.value)}
                data-testid="manual-res-people"
                className="mt-1.5" required
              />
            </div>
          </div>

          <div>
            <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">NOTE</Label>
            <textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              data-testid="manual-res-notes"
              rows={2}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-[#1E3A5D]/20 text-sm bg-white"
              placeholder="Compleanno, allergie, richieste particolari..."
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#1E3A5D]/80" style={{ display: isEdit ? "none" : undefined }}>
            <input
              type="checkbox"
              checked={form.force}
              onChange={(e) => update("force", e.target.checked)}
              data-testid="manual-res-force"
              className="w-4 h-4 accent-[#B59410]"
            />
            <span>Forza inserimento (ignora limiti di capacità)</span>
          </label>
        </div>

        <div className="mt-6 flex gap-2">
          <Button
            type="button" onClick={onClose} variant="outline"
            data-testid="manual-res-cancel-btn"
            className="flex-1 rounded-full font-display text-xs tracking-wider"
          >
            Annulla
          </Button>
          <Button
            type="submit" disabled={loading}
            data-testid="manual-res-submit-btn"
            className="flex-1 rounded-full bg-[#B59410] hover:bg-[#8E7308] text-white font-display text-xs tracking-[0.18em] font-semibold"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Salva modifiche" : "Salva"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ---------- Dashboard ----------
const AdminDashboard = ({ token, onLogout }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [recent, setRecent] = useState(null);
  const [overviewTab, setOverviewTab] = useState("recent"); // "recent" | "upcoming"
  const [overviewOpen, setOverviewOpen] = useState(false); // collapsed by default
  const [loading, setLoading] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingRes, setEditingRes] = useState(null);

  const dateIso = format(selectedDate, "yyyy-MM-dd");

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, upRes, recRes] = await Promise.all([
        axios.get(`${API}/admin/summary`, {
          params: { date: dateIso },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/upcoming`, {
          params: { limit: 20 },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/recent`, {
          params: { limit: 15 },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setSummary(sumRes.data);
      setUpcoming(upRes.data);
      setRecent(recRes.data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Errore caricamento");
    } finally { setLoading(false); }
  }, [dateIso, token, onLogout]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleUpdate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reservations/${id}`, { status },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Prenotazione ${STATUS_LABEL[status].toLowerCase()}`);
      fetchSummary();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/reservations/${id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prenotazione eliminata");
      fetchSummary();
    } catch { toast.error("Errore eliminazione"); }
  };

  const totalPeople = summary?.turns?.reduce((a, t) => a + t.used_people, 0) || 0;
  const totalRes = summary?.turns?.reduce((a, t) => a + t.reservations.filter(r => r.status !== "cancelled").length, 0) || 0;

  return (
    <div className="min-h-screen bg-[#F5EFE4]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-[#1E3A5D]/10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo-mama.png" alt="Mama" className="h-10 shrink-0" />
            <span className="font-display text-xs text-[#1E3A5D] tracking-[0.2em] font-semibold hidden sm:inline">
              PANNELLO PRENOTAZIONI
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowManualModal(true)}
              data-testid="add-manual-res-btn"
              className="rounded-full text-xs font-display tracking-wider bg-[#B59410] hover:bg-[#8E7308] text-white font-semibold"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">Nuova prenotazione</span><span className="sm:hidden">Nuova</span>
            </Button>
            <Button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              variant="outline"
              className="rounded-full text-xs font-display tracking-wider"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Date nav */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <TelegramCard token={token} />
        <div className="mt-6 bg-white rounded-2xl border border-[#1E3A5D]/10 shadow-sm p-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setSelectedDate(subDays(selectedDate, 1))}
            data-testid="date-prev-btn"
            className="w-10 h-10 rounded-full grid place-items-center border border-[#1E3A5D]/15 hover:bg-[#F5EFE4] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 text-center">
            <p className="font-script text-3xl sm:text-4xl text-[#1E3A5D] leading-none">
              {format(selectedDate, "EEEE d MMMM", { locale: it })}
            </p>
            <p className="mt-1 text-xs text-[#1E3A5D]/60 font-display tracking-wider">
              {format(selectedDate, "yyyy")} · {totalRes} prenotazioni · {totalPeople} coperti
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedDate(new Date())}
              data-testid="date-today-btn"
              className="hidden sm:block px-3 py-1.5 text-xs font-display tracking-wider rounded-full border border-[#1E3A5D]/15 hover:bg-[#F5EFE4]"
            >
              OGGI
            </button>
            <input
              type="date"
              value={dateIso}
              onChange={(e) => setSelectedDate(parseISO(e.target.value))}
              data-testid="date-picker-input"
              className="px-2 py-1.5 text-sm border border-[#1E3A5D]/15 rounded-md text-[#1E3A5D]"
            />
            <button
              onClick={() => setSelectedDate(addDays(selectedDate, 1))}
              data-testid="date-next-btn"
              className="w-10 h-10 rounded-full grid place-items-center border border-[#1E3A5D]/15 hover:bg-[#F5EFE4] transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="mt-6 text-center text-[#1E3A5D]/50">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Caricamento…
          </div>
        )}

        {/* Overview: recently received + upcoming (collapsible) */}
        {!loading && (recent?.count > 0 || upcoming?.count > 0) && (
          <section data-testid="overview-section" className="mt-6 bg-white rounded-2xl border border-[#B59410]/30 shadow-sm">
            <button
              type="button"
              onClick={() => setOverviewOpen((v) => !v)}
              data-testid="overview-toggle"
              className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 hover:bg-[#F5EFE4]/50 rounded-2xl transition-colors"
            >
              <div className="flex items-center gap-3 text-left">
                <div className="w-9 h-9 rounded-full bg-[#B59410]/15 grid place-items-center">
                  <CalIcon className="w-4 h-4 text-[#B59410]" />
                </div>
                <div>
                  <p className="font-script text-xl text-[#1E3A5D] leading-none">
                    Prenotazioni ricevute
                  </p>
                  <p className="mt-1 text-xs text-[#1E3A5D]/60 font-serif-body">
                    {recent?.count || 0} totali · {upcoming?.count || 0} da oggi in poi
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-[#1E3A5D]/60 transition-transform ${
                  overviewOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {overviewOpen && (
              <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-[#1E3A5D]/10 pt-4">
                <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                  <div className="flex gap-1 bg-[#F5EFE4] p-1 rounded-full">
                    <button
                      onClick={() => setOverviewTab("recent")}
                      data-testid="tab-recent"
                      className={`px-4 py-1.5 text-xs font-display tracking-wider rounded-full transition-colors ${
                        overviewTab === "recent"
                          ? "bg-[#1E3A5D] text-white"
                          : "text-[#1E3A5D] hover:bg-white"
                      }`}
                >
                  RICEVUTE DI RECENTE
                </button>
                <button
                  onClick={() => setOverviewTab("upcoming")}
                  data-testid="tab-upcoming"
                  className={`px-4 py-1.5 text-xs font-display tracking-wider rounded-full transition-colors ${
                    overviewTab === "upcoming"
                      ? "bg-[#1E3A5D] text-white"
                      : "text-[#1E3A5D] hover:bg-white"
                  }`}
                >
                  PROSSIME
                </button>
              </div>
              <p className="text-xs text-[#1E3A5D]/60 font-serif-body italic">
                {overviewTab === "recent"
                  ? `${recent?.count || 0} prenotazioni totali · più recenti in cima`
                  : `${upcoming?.count || 0} da oggi in poi`}
              </p>
            </div>

            {overviewTab === "recent" && recent?.reservations?.length > 0 && (
              <div data-testid="recent-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {recent.reservations.map((r) => {
                  const isPast = r.date < (upcoming?.today || dateIso);
                  return (
                    <button
                      key={`rec-${r.id}`}
                      onClick={() => setSelectedDate(parseISO(r.date))}
                      data-testid={`recent-item-${r.id}`}
                      className="text-left p-3 rounded-lg border border-[#1E3A5D]/10 hover:border-[#B59410]/50 hover:bg-[#F5EFE4] transition-colors relative"
                    >
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <p className="font-display text-xs text-[#B59410] tracking-wider font-semibold">
                          {format(parseISO(r.date), "EEE d MMM", { locale: it }).toUpperCase()} · {r.time}
                        </p>
                        {isPast && (
                          <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold">
                            passata
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#1E3A5D] font-semibold truncate">{r.name}</p>
                      <p className="text-xs text-[#1E3A5D]/60 truncate">
                        {r.people} {r.people === 1 ? "persona" : "persone"} · {r.phone}
                      </p>
                      {r.created_at && (
                        <p className="mt-1 text-[10px] text-[#1E3A5D]/40 font-serif-body italic">
                          Ricevuta {format(parseISO(r.created_at), "d MMM 'alle' HH:mm", { locale: it })}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {overviewTab === "upcoming" && upcoming?.count > 0 && (
              <>
                {upcoming.by_date && upcoming.by_date.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {upcoming.by_date.slice(0, 10).map((d) => (
                      <button
                        key={d.date}
                        onClick={() => setSelectedDate(parseISO(d.date))}
                        data-testid={`upcoming-date-${d.date}`}
                        className={`px-2.5 py-1 text-[11px] font-display tracking-wider rounded-full border transition-colors ${
                          d.date === dateIso
                            ? "bg-[#B59410] text-white border-[#B59410]"
                            : "border-[#1E3A5D]/15 hover:bg-[#F5EFE4] text-[#1E3A5D]"
                        }`}
                      >
                        {format(parseISO(d.date), "d MMM", { locale: it })} · {d.reservations}
                      </button>
                    ))}
                  </div>
                )}
                <div data-testid="upcoming-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {upcoming.reservations.slice(0, 12).map((r) => (
                    <button
                      key={`up-${r.id}`}
                      onClick={() => setSelectedDate(parseISO(r.date))}
                      data-testid={`upcoming-item-${r.id}`}
                      className="text-left p-3 rounded-lg border border-[#1E3A5D]/10 hover:border-[#B59410]/50 hover:bg-[#F5EFE4] transition-colors"
                    >
                      <p className="font-display text-xs text-[#B59410] tracking-wider font-semibold">
                        {format(parseISO(r.date), "EEE d MMM", { locale: it }).toUpperCase()} · {r.time}
                      </p>
                      <p className="mt-0.5 text-sm text-[#1E3A5D] font-semibold truncate">{r.name}</p>
                      <p className="text-xs text-[#1E3A5D]/60 truncate">
                        {r.people} {r.people === 1 ? "persona" : "persone"} · {r.phone}
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}

            {overviewTab === "upcoming" && upcoming?.count === 0 && (
              <p className="text-center py-6 text-[#1E3A5D]/50 font-serif-body italic text-sm">
                Nessuna prenotazione futura al momento.
              </p>
            )}
              </div>
            )}
          </section>
        )}

        {!loading && recent?.count === 0 && upcoming?.count === 0 && (
          <div data-testid="no-upcoming" className="mt-6 bg-white rounded-2xl border border-[#1E3A5D]/10 shadow-sm p-8 text-center">
            <p className="font-script text-2xl text-[#1E3A5D]">Nessuna prenotazione ancora</p>
            <p className="mt-2 text-sm text-[#1E3A5D]/60 font-serif-body">
              Le nuove prenotazioni compariranno qui automaticamente.
            </p>
          </div>
        )}

        {!loading && summary && totalRes === 0 && (
          <div data-testid="no-res-selected-date" className="mt-6 bg-[#F5EFE4] border border-dashed border-[#1E3A5D]/20 rounded-2xl p-6 text-center">
            <p className="font-serif-body text-sm text-[#1E3A5D]/70 italic">
              Nessuna prenotazione per <strong>{format(selectedDate, "d MMMM", { locale: it })}</strong>
              {(recent?.count > 0 || upcoming?.count > 0) ? (
                <>
                  {" · Consulta la sezione qui sopra oppure "}
                  <button
                    data-testid="add-manual-from-empty"
                    onClick={() => setShowManualModal(true)}
                    className="underline hover:text-[#B59410]"
                  >aggiungi una nuova prenotazione</button>
                </>
              ) : (
                " · Usa il pulsante '+ Nuova prenotazione' in alto per aggiungerne una"
              )}
              .
            </p>
          </div>
        )}

        {!loading && summary?.turns?.map((t) => {
          if (totalRes === 0) return null;
          const activeRes = t.reservations.filter(r => r.status !== "cancelled");
          const pctPeople = Math.round((t.used_people / t.max_people) * 100);
          const isFull = t.used_people >= t.max_people || t.used_tables >= t.max_tables;
          return (
            <section key={t.turn_id} data-testid={`turn-${t.turn_id}`} className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="font-script text-2xl text-[#1E3A5D] leading-none">{t.label}</h2>
                  <p className="mt-1 text-xs text-[#1E3A5D]/60 font-display tracking-wider">
                    {activeRes.length} prenotazioni · {t.reservations.filter(r => r.status === "cancelled").length} annullate
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-sm text-[#1E3A5D] font-semibold">
                    <Users className="w-3.5 h-3.5 inline mr-1 text-[#B59410]" />
                    {t.used_people} / {t.max_people}
                  </p>
                  <p className="text-xs text-[#1E3A5D]/60 mt-0.5">
                    <Clock className="w-3 h-3 inline mr-0.5" /> {t.used_tables}/{t.max_tables} tavoli
                  </p>
                </div>
              </div>

              {/* Capacity bar */}
              <div className="h-2 bg-[#1E3A5D]/10 rounded-full overflow-hidden mb-4">
                <div
                  className={`h-full transition-all ${isFull ? "bg-rose-500" : pctPeople > 75 ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, pctPeople)}%` }}
                />
              </div>

              {t.reservations.length === 0 ? (
                <p className="text-center py-6 text-[#1E3A5D]/40 font-serif-body italic text-sm">
                  Nessuna prenotazione per questo turno.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {t.reservations.map((r) => (
                    <ReservationCard key={r.id} r={r} onUpdate={handleUpdate} onDelete={handleDelete} onEdit={(res) => setEditingRes(res)} />
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {showManualModal && (
        <ManualReservationModal
          token={token}
          defaultDate={dateIso}
          onClose={() => setShowManualModal(false)}
          onCreated={fetchSummary}
        />
      )}

      {editingRes && (
        <ManualReservationModal
          token={token}
          defaultDate={dateIso}
          editRes={editingRes}
          onClose={() => setEditingRes(null)}
          onCreated={fetchSummary}
        />
      )}
    </div>
  );
};

// ---------- Root ----------
export const AdminApp = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} onLogout={handleLogout} />;
};
